import {
  AUTO_APPROVAL_THRESHOLD_MAX_LENGTH,
  REGEX_DECIMAL_NUMBERS,
  REGEX_MONEY_FORMAT_SPACING,
  REGEX_SUM_OF_MONEY
} from 'utils/constants/constants';

export const formatMoney = (amount?: number): string => {
  if (!amount) {
    return '0.00';
  }
  const [integerPart, decimalPart] = amount.toFixed(2).split('.');
  const formattedIntegerPart = integerPart.replace(
    REGEX_MONEY_FORMAT_SPACING,
    '\xa0'
  );
  return `${formattedIntegerPart}.${decimalPart}`;
};

export const processMoneyInput = (
  value: string,
  prevValue: string,
  maxCharacters?: number
) => {
  if (value === '') {
    return '0';
  }

  if (maxCharacters && value.length > maxCharacters) {
    return prevValue;
  }

  const valueWithLeadingZero = value[0] === '.' ? `0${value}` : value;

  const cleanedValue =
    valueWithLeadingZero.length >= 2 &&
    valueWithLeadingZero[0] === '0' &&
    valueWithLeadingZero[1] !== '.'
      ? valueWithLeadingZero.slice(1)
      : valueWithLeadingZero;

  const isValidValue =
    cleanedValue === '' || REGEX_DECIMAL_NUMBERS.test(cleanedValue);

  if (
    !isValidValue ||
    cleanedValue.length > AUTO_APPROVAL_THRESHOLD_MAX_LENGTH
  ) {
    return prevValue;
  }

  const isValidMoneyValue =
    !Number.isNaN(Number(cleanedValue)) &&
    REGEX_SUM_OF_MONEY.test(cleanedValue);

  return isValidMoneyValue ? cleanedValue : prevValue;
};

export const formatNumberWithSpaces = (value: number): string => {
  return new Intl.NumberFormat('fr-FR').format(value);
};

export const round2 = (value: number): number => Number(value.toFixed(2));

export const formatMoneyWithCurrency = (
  amount?: number,
  currency?: string
): string => {
  if (!amount) return `0.00 ${currency || 'EUR'}`;
  return `${formatMoney(amount)} ${currency || 'EUR'}`;
};

const eurNumberFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

// Unlike formatMoney, a missing amount renders as empty rather than 0.00 —
// for converted EUR values null means "not computed", not zero.
export const formatEurAmount = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return eurNumberFormat.format(value);
};

export const formatEurWithSuffix = (
  value: number | null | undefined
): string => {
  const formatted = formatEurAmount(value);

  return formatted ? `${formatted} EUR` : '';
};
